import { motion } from "framer-motion";
import { useEffect, useState, useRef, useMemo } from "react";
import Image from "next/image";
import { hazards } from './hazards';
import { useGame } from "@/app/providers/GameContextProvider";
import { useTranslation } from "@/hooks/useTranslation";
import HazardIcon from "../HazardIcon";
import { getRandomWallpaper } from "@/lib/utils/wallpaperUtils";
import { useAudio, AUDIO } from '@/app/providers/AudioProvider';

interface RoundCountdownProps {
    onComplete: () => void;
    duration?: number;
}

const RoundCountdown = ({ onComplete, duration = 3 }: RoundCountdownProps) => {
    const { gameState, players } = useGame();
    const { t } = useTranslation();
    const { play } = useAudio();
    const [count, setCount] = useState<number | null>(null); // null = waiting, number = countdown
    const [imageLoaded, setImageLoaded] = useState(false);
    const countdownStartedRef = useRef(false);
    const lastRoundRef = useRef<number | null>(null);

    // Check if all players have loaded
    const allPlayersLoaded = gameState?.allPlayersLoadedPhotosphere ?? false;

    // Reset state when round changes
    useEffect(() => {
        const currentRound = gameState?.currentRound ?? 0;
        if (lastRoundRef.current !== null && lastRoundRef.current !== currentRound) {
            // Round changed, reset everything
            countdownStartedRef.current = false;
            setCount(null);
            setImageLoaded(false);
        }
        lastRoundRef.current = currentRound;
    }, [gameState?.currentRound]);

    // Count players who have loaded (for display)
    const loadedCount = useMemo(() => {
        return players.filter(p => p.photosphereLoaded && !p.isEliminated).length;
    }, [players]);

    const totalCount = useMemo(() => {
        return players.filter(p => !p.isEliminated).length;
    }, [players]);

    // Get wallpaper based on selected expansions, game seed, and current round
    const wallpaperSrc = useMemo(() => {
        const expansions = gameState?.selectedExpansions ? Array.from(gameState.selectedExpansions) : [];
        const seed = gameState?.gameSettings?.gameSeed || "default";
        const currentRound = gameState?.currentRound || 1;
        return getRandomWallpaper(expansions, seed, currentRound);
    }, [gameState?.selectedExpansions, gameState?.gameSettings?.gameSeed, gameState?.currentRound]);

    // Use ref to avoid re-running effect when onComplete changes
    const onCompleteRef = useRef(onComplete);
    onCompleteRef.current = onComplete;

    // Start countdown when all players have loaded
    useEffect(() => {
        if (allPlayersLoaded && !countdownStartedRef.current) {
            countdownStartedRef.current = true;
            setCount(duration);
            play(AUDIO.TICK);
            console.log("All players loaded, starting countdown");
        }
    }, [allPlayersLoaded, duration, play]);

    // Countdown timer
    useEffect(() => {
        if (count === null) return; // Still waiting

        if (count > 0) {
            const timer = setTimeout(() => {
                const newCount = count - 1;
                setCount(newCount);
                if (newCount > 0) {
                    play(AUDIO.TICK);
                } else {
                    play(AUDIO.COMPLETE);
                }
            }, 1000);
            return () => clearTimeout(timer);
        } else {
            onCompleteRef.current();
        }
    }, [count, play]);

    return (
        <motion.div
            className="absolute inset-0 z-50 flex flex-col items-center justify-center anti-flashbang"
            initial={{ opacity: 1 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
        >
            {/* Wallpaper */}
            <Image
                src={wallpaperSrc}
                alt="Wallpaper Background"
                layout="fill"
                objectFit="cover"
                quality={100}
                className={`absolute inset-0 z-0 blur-sm transition-opacity duration-300 ${imageLoaded ? 'opacity-100' : 'opacity-0'}`}
                onLoad={() => setImageLoaded(true)}
            />
            <div className="absolute inset-0 z-10 bg-gradient-to-t from-[#030b18]/80 to-transparent"></div>

            {/* Content area - either waiting or countdown */}
            <div className="relative z-20 flex flex-col items-center">
                {count === null ? (
                    /* Waiting for players state */
                    <motion.div
                        key="waiting"
                        initial={{ scale: 0.8, opacity: 0 }}
                        animate={{ scale: 1, opacity: 1 }}
                        transition={{ duration: 0.3, delay: 1 }}
                        className="flex flex-col items-center gap-4"
                    >
                        <Image
                            src="/loading.webp"
                            alt="Loading"
                            width={100}
                            height={100}
                            className="animate-spin"
                        />
                        {totalCount > 1 ? (
                            <>
                                {/* Waiting text */}
                                <div className="text-white text-2xl font-bold text-shadow-border shadow-black">
                                    {t('play.waitingForPlayers')}
                                </div>

                                {/* Player count */}
                                <div className="text-white/70 text-lg">
                                    {loadedCount} / {totalCount}
                                </div>
                            </>
                        ) : (
                            <div
                                className="text-white text-2xl font-bold text-shadow-border shadow-black flex flex-col items-center gap-4"
                            >
                                Loading...
                            </div>
                        )}
                    </motion.div>
                ) : (
                    /* Countdown state */
                    <motion.div
                        key={`countdown-${count}`}
                        initial={{ scale: 1.5, opacity: 0, filter: "brightness(100%)" }}
                        animate={{ scale: [1.5, 1, 1, 1], opacity: [0, 1, 1, 1], filter: ["brightness(100%)", "brightness(100%)", "brightness(200%)", "brightness(100%)"] }}
                        transition={{ duration: 0.7, times: [0, 0.5, 0.75, 1] }}
                        className="text-white text-9xl font-bold w-[200px] h-[200px] flex items-center justify-center"
                    >
                        {(count >= 1 && count <= 3) && (
                            <Image
                                src={`/countdown/${count}.png`}
                                alt={"Countdown " + count}
                                width={100}
                                height={100}
                            />
                        )}
                    </motion.div>
                )}
            </div>

            {/* Hazards display - shown in both states */}
            <div className="mt-5 relative z-20">
                <div className="flex flex-wrap justify-center gap-7">
                    {gameState?.currentHazards.map((hazardKey, index) => {
                        const hazard = hazards.find(h => h.name === hazardKey.toLowerCase());
                        if (!hazard) return null;
                        return (
                            <div
                                key={`hazard-icon-${index}`}
                                className="flex flex-col gap-1 items-center text-white bg-black/40 rounded-xl px-4 py-1 backdrop-blur-sm"
                            >
                                <HazardIcon hazard={hazard} tooltip={false} />
                                <span className="text-shadow-border shadow-black">{t(`hazards.${hazard.name}`)}</span>
                            </div>
                        );
                    })}
                </div>
            </div>
        </motion.div>
    );
};

export default RoundCountdown;